/**
 * Stand als eine Zeile im Projektkopf — „Wo steht das Projekt, in einem Satz?"
 *
 * Ein Eintrag der Projektakte (`projekt_akte_eintrag`, `typ: stand`). Es gilt der
 * neueste; der vorige bleibt als „frühere" nachlesbar. Übernommen aus dem
 * früheren Stand-Block (ProjektStand), nur kompakter: Stift → Feld.
 */
import { useState } from "react";
import { fehlerText } from "@vimaana/ui";

import { useCreateProjektAkteEintrag, useProjektAkte } from "../../hooks/usePlanung";

function wann(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function wer(adresse: string): string {
  const lokal = (adresse || "").split("@")[0];
  return lokal ? lokal.charAt(0).toUpperCase() + lokal.slice(1) : "";
}

export default function KopfStand({ projektId }: { projektId: string }) {
  const akte = useProjektAkte(projektId);
  const anlegen = useCreateProjektAkteEintrag(projektId);
  const [schreiben, setSchreiben] = useState(false);
  const [text, setText] = useState("");
  const [frueher, setFrueher] = useState(false);

  const staende = (akte.data?.eintraege || [])
    .filter((e) => e.typ === "stand")
    .sort((a, b) => (a.ereignis_am < b.ereignis_am ? 1 : -1));
  const aktuell = staende.find((e) => e.status === "aktiv") || null;
  const alte = staende.filter((e) => e !== aktuell);
  const bearbeiten = schreiben || (!aktuell && !akte.isLoading);

  const festhalten = () => {
    const titel = text.trim();
    if (!titel) return;
    anlegen.mutate({ typ: "stand", titel }, { onSuccess: () => { setText(""); setSchreiben(false); } });
  };

  return (
    <div className="pk-stand">
      <div className="pk-spaltenkopf">
        <span className="pk-eyebrow">Stand</span>
        {!bearbeiten && (
          <button type="button" className="pk-still" onClick={() => { setText(""); setSchreiben(true); }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></svg>
            <span>Neuer Stand</span>
          </button>
        )}
      </div>

      {!bearbeiten && aktuell && (
        <>
          <p className="pk-stand-text">„{aktuell.titel}“</p>
          <p className="pk-meta">
            {wann(aktuell.ereignis_am)}{aktuell.erstellt_von ? ` · ${wer(aktuell.erstellt_von)}` : ""}
            {alte.length > 0 && (
              <>
                {" · "}
                <button type="button" className="pk-link" aria-expanded={frueher} onClick={() => setFrueher((v) => !v)}>
                  {frueher ? "frühere verbergen" : `${alte.length} frühere`}
                </button>
              </>
            )}
          </p>
        </>
      )}

      {bearbeiten && (
        <div className="pk-stand-eingabe">
          <label className="pk-feldlabel" htmlFor={`stand-${projektId}`}>Wo steht das Projekt, in einem Satz?</label>
          <textarea
            id={`stand-${projektId}`}
            className="pk-stand-feld"
            rows={2}
            maxLength={300}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) festhalten(); }}
            placeholder="z. B. „Material bestellt, Lieferung zugesagt für Donnerstag.“"
            autoFocus={schreiben}
          />
          <div className="pk-knopfreihe">
            <button type="button" className="vg-btn vg-btn-primary" disabled={anlegen.isPending || !text.trim()} onClick={festhalten}>Festhalten</button>
            {schreiben && <button type="button" className="vg-btn vg-btn-ghost" onClick={() => setSchreiben(false)}>Abbrechen</button>}
          </div>
        </div>
      )}

      {frueher && alte.length > 0 && (
        <ol className="pk-frueher">
          {alte.map((e) => (
            <li key={e.id}>
              <span>{e.titel}</span>
              <small>{wann(e.ereignis_am)}{e.erstellt_von ? ` · ${wer(e.erstellt_von)}` : ""}</small>
            </li>
          ))}
        </ol>
      )}
      {anlegen.error && <p className="pk-fehler">{fehlerText(anlegen.error)}</p>}
    </div>
  );
}
